import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { ParsedMarkdown } from '../types';
import { buildGraph } from '../utils/graphBuilder';
import { applyLayout } from '../utils/layoutEngine';
import {
  NodeFilters,
  applyFilters,
  createDefaultFilters,
  calculateFilterStats,
} from '../utils/nodeFilters';
import QANodeComponent from './QANodeComponent';
import ConflictPanel from './ConflictPanel';
import ExportControls from './ExportControls';
import FilterPanel from './FilterPanel';

interface MindMapProps {
  parsedData: ParsedMarkdown;
  fileName: string;
}

const nodeTypes = {
  qaNode: QANodeComponent,
};

export default function MindMap({ parsedData, fileName }: MindMapProps) {
  // Initialize filters
  const [filters, setFilters] = useState<NodeFilters>(() =>
    createDefaultFilters(parsedData.qaPairs)
  );

  // Apply filters to get visible QA pairs
  const filteredQAPairs = useMemo(() => {
    return applyFilters(parsedData.qaPairs, filters);
  }, [parsedData.qaPairs, filters]);

  // Calculate filter statistics
  const filterStats = useMemo(() => {
    return calculateFilterStats(parsedData.qaPairs, filteredQAPairs);
  }, [parsedData.qaPairs, filteredQAPairs]);

  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const { nodes, edges } = buildGraph(
      filteredQAPairs, // Visible nodes
      parsedData.conflicts,
      {
        enableSiblingGrouping: false,
        showGhostNodes: filters.showGhostNodes,
        allQAPairs: parsedData.qaPairs, // Full list for dependency matching
      }
    );
    const layoutedNodes = applyLayout(nodes, edges);

    return { nodes: layoutedNodes, edges };
  }, [filteredQAPairs, parsedData.conflicts, parsedData.qaPairs, filters.showGhostNodes]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes and edges when filtered data changes
  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = buildGraph(
      filteredQAPairs, // Visible nodes
      parsedData.conflicts,
      {
        enableSiblingGrouping: false,
        showGhostNodes: filters.showGhostNodes,
        allQAPairs: parsedData.qaPairs, // Full list for dependency matching
      }
    );
    const layoutedNodes = applyLayout(newNodes, newEdges);

    setNodes(layoutedNodes);
    setEdges(newEdges);
  }, [filteredQAPairs, parsedData.conflicts, parsedData.qaPairs, filters.showGhostNodes, setNodes, setEdges]);

  // Reset filters when parsed data changes
  useEffect(() => {
    setFilters(createDefaultFilters(parsedData.qaPairs));
  }, [parsedData.qaPairs]);

  const handleConflictClick = useCallback(
    (nodeIds: string[]) => {
      // Highlight the affected nodes
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          style: {
            ...node.style,
            boxShadow: nodeIds.includes(node.id)
              ? '0 0 0 3px #ef4444'
              : undefined,
          },
        }))
      );

      // Focus on first affected node
      if (nodeIds.length > 0) {
        const firstNode = nodes.find((n) => n.id === nodeIds[0]);
        if (firstNode) {
          // Use setTimeout to allow ReactFlow to update
          setTimeout(() => {
            const rfElement = document.querySelector('.react-flow__viewport') as HTMLElement;
            if (rfElement) {
              // Simple centering - in production you'd want to use ReactFlow's fitView
              console.log('Focusing on node:', firstNode.id);
            }
          }, 100);
        }
      }
    },
    [nodes, setNodes]
  );

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{
          padding: 0.2,
        }}
        minZoom={0.1}
        maxZoom={1.5}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e2e8f0" />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={(node) => {
            if (node.data?.hasConflict) return '#ef4444';
            const depth = node.data?.qaPair?.question?.depth;
            if (depth === 'L1') return '#3b82f6'; // Blue
            if (depth === 'L2') return '#10b981'; // Green
            if (depth === 'L3') return '#8b5cf6'; // Purple
            if (depth === 'L4') return '#f59e0b'; // Amber
            if (depth === 'L5') return '#ec4899'; // Pink
            return '#6b7280';
          }}
          maskColor="rgba(0, 0, 0, 0.1)"
          style={{
            background: '#f8fafc',
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
          }}
        />
      </ReactFlow>

      {/* Filter Panel (top-right) */}
      <FilterPanel
        qaPairs={parsedData.qaPairs}
        filters={filters}
        onFiltersChange={setFilters}
        stats={filterStats}
      />

      {/* Conflict Panel (bottom-right) */}
      <ConflictPanel
        conflicts={parsedData.conflicts}
        onConflictClick={handleConflictClick}
      />

      <ExportControls parsedData={parsedData} fileName={fileName} />

      {/* File Info Header */}
      <div
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          background: '#ffffff',
          border: '2px solid #e2e8f0',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          zIndex: 10,
        }}
      >
        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
          Visualizing:
        </div>
        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b' }}>
          {fileName}
        </div>
      </div>
    </div>
  );
}
